<script setup lang="ts">
import { ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useGetUser } from '@ilteducation/customer-mgmt';
import Modal from '../Modal.vue';
import CreateSingleUser from './CreateSingleUser.vue';
import Confirmation from '../Confirmation/index.vue';
import LoginCard from '../LoginCard/LoginCard.vue';
import { usePrintLoginCard } from '../../composables/useDownloadLoginCard';
import useQrCode from '../../composables/useQrCode';
import useModal, { ModalTypes } from '../../composables/useModal';
import { useLocale } from '../../composables/useLocale';
import { Role } from '../../types/user';
import { useSelectedItems } from '../../composables/useSelectedItems';
import type { SlimOrganization } from '@ilteducation/my-account-types';

const { t } = useLocale();

type NewCreatedUser = {
  uid: string;
  userName: string;
  name: string;
  password: string;
  qrToken?: string;
  role: Role;
};

type Props = {
  name: ModalTypes;
  organization: SlimOrganization;
};
const props = defineProps<Props>();

type Emits = {
  userCreated: [uid: string];
  createMoreUsersClick: [];
};
const emit = defineEmits<Emits>();

const { isOpen, close } = useModal(props.name);
const { xs } = useDisplay();

const formRef = ref<typeof CreateSingleUser>();
const isCreating = ref(false);
const newCreatedUser = ref<NewCreatedUser | null>(null);

enum States {
  CREATE_USER,
  CONFIRMATION,
}

const state = ref<States>(States.CREATE_USER);

const qrCodeLoginLink = ref<string | undefined>(undefined);
const qrCodeImageData = ref<string | undefined>(undefined);
const { createLoginLink, createQrCodeAsSVGDataURL } = useQrCode();

const { setSelectedItems } = useSelectedItems();
const { appLogos, fullAppUrl, iltIcon, download } = usePrintLoginCard([], '');
const { loadUser } = useGetUser();

const createUser = async () => {
  isCreating.value = true;
  const user = await formRef.value?.createUser();

  if (!user) {
    isCreating.value = false;
    return;
  }

  const newUser = await loadUser(user.uid);
  if (!newUser) {
    isCreating.value = false;
    return;
  }

  newCreatedUser.value = { ...user, userName: newUser.userName };

  if (user.qrToken) {
    qrCodeLoginLink.value = createLoginLink(fullAppUrl.value, user.uid, user.qrToken);
    qrCodeImageData.value = await createQrCodeAsSVGDataURL(qrCodeLoginLink.value, iltIcon);
  }

  state.value = States.CONFIRMATION;
  emit('userCreated', user.uid);
  isCreating.value = false;
};

const reset = () => {
  newCreatedUser.value = null;
  state.value = States.CREATE_USER;
};
const resetAndClose = () => {
  reset();
  close();
};

const onCreateMoreUsers = () => {
  reset();
  close();
  emit('createMoreUsersClick');
};

const isDownloading = ref(false);
const onDownloadLoginCardClick = async () => {
  isDownloading.value = true;
  if (newCreatedUser.value) {
    await download('new-user-login-card', newCreatedUser.value.userName);
  }
  isDownloading.value = false;
};

const isNonEmployee = computed(() => ['student', 'guardian'].includes(newCreatedUser.value?.role || ''));

const modalTitle = computed(() =>
  state.value === States.CREATE_USER ? t('lcp.create_a_new_user') : t('lcp.new_user_created'),
);

const goToUserProfile = () => {
  if (newCreatedUser.value) {
    setSelectedItems([newCreatedUser.value.uid]);
  }
  reset();
  close();
};
</script>

<template>
  <Modal
    :name="name"
    :title="modalTitle"
    title-class="text-h5"
    :is-open="isOpen"
    :loading="isCreating"
    :show-close-btn="false"
    :show-cancel-action="false"
    :top-left-btn-action="state === States.CREATE_USER ? close : undefined"
    :top-left-btn-label="t('lcp.close')"
    top-left-btn-icon="mdi-close"
    contained
    fullscreen
    background-color="rgba(var(--v-theme-surface-container))"
    @close="resetAndClose"
  >
    <template v-if="state === States.CREATE_USER">
      <v-sheet
        v-if="isOpen"
        max-width="740"
        rounded
        class="mx-auto bg-white"
        :class="{ 'bg-surface-container': xs, 'pa-10': !xs }"
      >
        <CreateSingleUser ref="formRef" :organization="organization" :loading="isCreating" />
      </v-sheet>
      <v-sheet v-if="isOpen" max-width="740" class="d-flex justify-end mx-auto bg-surface-container py-6">
        <v-btn :block="xs" :disabled="isCreating" variant="flat" append-icon="mdi-chevron-right" @click="createUser">
          {{ t('lcp.create_user') }}
        </v-btn>
      </v-sheet>
    </template>
    <template v-if="state === States.CONFIRMATION">
      <Confirmation
        @done-click="resetAndClose"
        @create-more-users-click="onCreateMoreUsers"
        @download-login-cards-click="onDownloadLoginCardClick"
        @goToProfileClick="goToUserProfile"
        :is-downloading-login-cards="isDownloading"
        :only-employee-created-mode="!isNonEmployee"
        :show-go-to-user-profile="true"
        :show-done="false"
        message="lcp.what_you_can_do_in_the_user_profile"
      />
      <div style="display: none">
        <LoginCard
          id="new-user-login-card"
          ref="loginCard"
          v-if="newCreatedUser"
          :name="newCreatedUser.name"
          :user-name="newCreatedUser.userName"
          :app-logo-imgs="appLogos"
          :app-url="fullAppUrl"
          :password="newCreatedUser.password"
          :qr-code-image="qrCodeImageData"
          :qr-code-login-url="qrCodeLoginLink"
        />
      </div>
    </template>
  </Modal>
</template>
